/**
 * Presentation helpers for the Sessions feature. Pure formatting only — all
 * lifecycle / geofence / km logic lives in the shared domain layer.
 */

import type {
  Session,
  SessionLifecycleState,
} from '@shared/sessions/types'
import { pushSafeFirstName } from '@shared/sessions/push-privacy'

/** Start of the local calendar day containing `ms`. */
export function startOfDay(ms: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

export function isToday(ms: number): boolean {
  return startOfDay(ms) === startOfDay(Date.now())
}

export function isUpcoming(ms: number): boolean {
  return startOfDay(ms) > startOfDay(Date.now())
}

export function isPast(ms: number): boolean {
  return startOfDay(ms) < startOfDay(Date.now())
}

/** Parse an AHP UTC-offset label like "+08:00" / "-09:30" into milliseconds. */
export function parseUtcOffsetMs(offset: string | undefined): number | null {
  if (!offset) return null
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(offset.trim())
  if (!m) return null
  const sign = m[1] === '-' ? -1 : 1
  return sign * (Number(m[2]) * 60 + Number(m[3])) * 60_000
}

/**
 * Clock time for a session timestamp. When the session's UTC-offset label is
 * given, the time is rendered in THAT offset — the UI prints the label next to
 * it (e.g. "9:00 am (+08:00)"), so rendering the device-local clock against
 * AHP's offset label showed two contradictory times for travelling providers.
 */
export function formatTime(ms: number, timezone?: string): string {
  const offsetMs = parseUtcOffsetMs(timezone)
  if (offsetMs == null) {
    return new Date(ms).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
  }
  return new Date(ms + offsetMs).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

export function formatTimeRange(start: number, end: number, timezone?: string): string {
  return `${formatTime(start, timezone)} – ${formatTime(end, timezone)}`
}

/** Provider-only note after the departure time when it isn't the tap time. */
export function departureTimeNote(endedBy: Session['endedBy']): string {
  if (endedBy === 'geofence-exit') return ' · when you left'
  if (endedBy === 'track-exit') return ' · adjusted from track'
  if (endedBy === 'call') return ' · call ended'
  return ''
}

/** Start of the local calendar day `n` days from the day containing `ms` (DST-safe). */
function startOfDayOffset(ms: number, days: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + days)
  return d.getTime()
}

export function formatDayHeading(ms: number): string {
  const now = Date.now()
  const today = startOfDay(now)
  const day = startOfDay(ms)
  if (day === today) return 'Today'
  // Calendar-day arithmetic, not `± 24h`: a DST transition makes the adjacent
  // day 23/25 hours long, which broke the Tomorrow/Yesterday labels twice a year.
  if (day === startOfDayOffset(now, 1)) return 'Tomorrow'
  if (day === startOfDayOffset(now, -1)) return 'Yesterday'
  return new Date(ms).toLocaleDateString('en-AU', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

export function formatFullDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/** Human distance: metres under 1km, otherwise km to 1 decimal. */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toFixed(1)} km`
}

export function formatKm(km: number | null): string | null {
  if (km == null) return null
  return `${km.toFixed(1)} km`
}

const LIFECYCLE_LABELS: Record<SessionLifecycleState, string> = {
  scheduled: 'Scheduled',
  reminded: 'Reminder sent',
  'check-in-available': 'Awaiting arrival',
  'checked-in': 'In session',
  tracking: 'In session',
  'checkout-reminded': 'Wrap-up reminder',
  'checked-out': 'Completed',
  synced: 'Synced',
  cancelled: 'Cancelled',
  'not-started': 'Not started',
}

export function lifecycleLabel(state: SessionLifecycleState): string {
  return LIFECYCLE_LABELS[state] ?? state
}

/** Tailwind classes for the lifecycle badge, keyed by state. */
export function lifecycleBadgeClasses(state: SessionLifecycleState): string {
  switch (state) {
    case 'checked-in':
    case 'tracking':
      return 'bg-emerald-100 text-emerald-800'
    case 'checked-out':
    case 'synced':
      return 'bg-slate-100 text-slate-600'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'not-started':
      return 'bg-amber-100 text-amber-800'
    case 'check-in-available':
      return 'bg-brand-100 text-brand-700'
    case 'reminded':
    case 'checkout-reminded':
      return 'bg-amber-100 text-amber-800'
    default:
      return 'bg-sky-100 text-sky-800'
  }
}

/** Sort comparator: earliest start first. */
export function byStartAsc(a: Session, b: Session): number {
  return a.start - b.start
}

/**
 * Terminal cancelled — by this app's lifecycle (provider confirmed a member's
 * request) OR by AHP's core status. Distinct from the shared
 * `isSessionCancelled`, which only reflects AHP's `coreStatus`.
 */
export function isTerminallyCancelled(session: Session): boolean {
  return session.lifecycleState === 'cancelled' || session.coreStatus === 'cancelled'
}

/** Member asked to cancel and the provider has not confirmed yet. */
export function isCancellationPending(session: Session): boolean {
  return !isTerminallyCancelled(session) && session.cancellationRequested != null
}

/**
 * List comparator: live sessions first (earliest start first), cancelled ones
 * after them — a cancelled session must never read as the next thing to do.
 * Pending-cancellation sessions stay in their time slot: they still need the
 * provider's decision.
 */
export function byActiveThenStart(a: Session, b: Session): number {
  const aCancelled = isTerminallyCancelled(a) ? 1 : 0
  const bCancelled = isTerminallyCancelled(b) ? 1 : 0
  if (aCancelled !== bCancelled) return aCancelled - bCancelled
  return a.start - b.start
}

/**
 * UI gating for the check-in action. The backend `update-session-state` accepts
 * a check-in from any pre-active state, so we allow it through the wrap-up
 * states but not once already checked-in/out.
 */
export function canCheckIn(state: SessionLifecycleState): boolean {
  return (
    state === 'scheduled' ||
    state === 'reminded' ||
    state === 'check-in-available' ||
    state === 'not-started'
  )
}

/** UI gating for the check-out action — only while a session is active. */
export function canCheckOut(state: SessionLifecycleState): boolean {
  return state === 'checked-in' || state === 'tracking' || state === 'checkout-reminded'
}

/** Follower list label, first names only: "Xavier · with Zach". */
export function followerSessionLabel(session: Pick<Session, 'memberName' | 'providerName'>): string {
  const member = pushSafeFirstName(session.memberName, 'Session')
  const provider = pushSafeFirstName(session.providerName)
  return provider ? `${member} · with ${provider}` : member
}

/** Follower detail heading: "Xavier's session". */
export function followerSessionHeading(session: Pick<Session, 'memberName'>): string {
  const member = pushSafeFirstName(session.memberName)
  return member ? `${member}'s session` : 'Session'
}
